import { FC, JSX, useContext, useMemo, useRef, useState } from "react";
import { ProviderProfileActionEnum, ServicingAccessPoint } from "@app/projects/network-assessment/provider-hub/interfaces";
import { AccessPointDetails } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/accessPointDetails";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ColumnConfig, numberSort, SortDirections, TextAlignment, wafflesKeyColumnName, WafflesThemes } from "@app/shared/waffles/exports";
import { formatSpecialtyWithCode, GeoInfoToDisplay } from "@app/projects/network-assessment/provider-hub/utility";
import { ProjectionProviderType, ProjectRunConfigurationType, ProviderActionStatusEnum } from "@app/projects/enumerators";
import ProviderHubProfileActionFlyout from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderHubProfileActionFlyout";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { MembershipType } from "@app/exports/enumerations";
import { useIsOverflow } from "@app/shared/qes-standards/utility/useIsOverflow";
import { outOfNetworkProviderNetworkTypes, ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { Accordion } from "@app/shared/Accordion";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { isNullOrUndefined } from "@app/shared/utility/isNullOrUndefined";
import { Waffles } from "@app/shared/waffles/Waffles";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { ViewSelectorItem, ViewSelectorKey, ViewSelectorList } from "@app/shared/data-summaries";
import { NetworkIntegrityNotificationFlag } from "@app/projects/network-assessment/provider-hub/network-integrity-components/NetworkIntegrityNotificationFlag";


interface AccessPointDetailsProps {
  accessPointDetails?: AccessPointDetails;
  isLoadingApDetails: boolean;
  selectedAccessPoint?: ServicingAccessPoint;
  hasNonMarketAccessPoints: boolean;
  onProviderActionSubmit?: () => void;
  projectionProviderType?: ProjectionProviderType;
  onSubmit?: () => void;
}

interface AccessPointServiceAreaData {
  [wafflesKeyColumnName]: string;
  gainsPercentDisplay: string;
  impactPercentDisplay: string;
  servesPercentDisplay: string;
  gainsPercent: number;
  impactPercent: number;
  servesPercent: number;
  passPercent: number;
  passPercentDisplay: string;
  serviceArea: JSX.Element;
  status: string;
  adequacyCriticalityScore: number;
  adequacyCriticalityScoreDisplay: string;
}

interface AccessPointNetworkIntegrityData {
  [wafflesKeyColumnName]: string;
  alert: string;
  outcome: JSX.Element;
};

interface AccessPointStillProcessingData {
  [wafflesKeyColumnName]: string;
  status: string;
};

const serviceAreaColumnConfig = (): ColumnConfig => {
  return {
    adequacyCriticalityScoreDisplay: {
      alignment: TextAlignment.Right,
      displayText: "Criticality Score",
      testId: "adequacy-criticality-score",
    },
    gainsPercentDisplay: {
      alignment: TextAlignment.Right,
      displayText: "Gains %",
      testId: "gains-percent",
      tooltip: "The increase to Access % if a user adds a provider at the location shown to their existing Network Providers file.",
    },
    impactPercentDisplay: {
      alignment: TextAlignment.Right,
      displayText: "Impact %",
      testId: "impact-percent",
      tooltip: "The new Total Access % if a provider at the location shown is added to the existing Network Providers file.",
    },
    passPercentDisplay: {
      alignment: TextAlignment.Right,
      displayText: "Min Pass %",
      testId: "min-pass-percent",
    },
    servesPercentDisplay: {
      alignment: TextAlignment.Right,
      displayText: "Serves %",
      testId: "serves-percent",
      tooltip: "The percentage of beneficiaries in a county/specialty combination that have access to the provider's access point.",
    },
    serviceArea: {
      displayText: "Service Area",
      noSort: true,
      sticky: true,
      testId: "service-area",
    },
    status: {
      displayText: "County Status",
      sticky: true,
      testId: "county-status",
    },
  }
}

const networkIntegrityColumnConfig = (): ColumnConfig => {
  return {
    alert: {
      displayText: "Alert",
      noSort: true,
      testId: "alert",
    },
    outcome: {
      displayText: "Outcome",
      noSort: true,
      testId: "outcome",
    }
  }
}

const networkIntegrityStillProcessingColumnConfig = (): ColumnConfig => {
  return {
    status: {
      displayText: "Status",
      noSort: true,
      testId: "status",
    }
  }
}

export const AccessPointDetailsSection: FC<AccessPointDetailsProps> = (props: AccessPointDetailsProps) => {
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const { accessPointDetails, isLoadingApDetails, selectedAccessPoint, hasNonMarketAccessPoints } = props;

  const providerNameRef = useRef<HTMLParagraphElement>(null);
  const addressRef = useRef<HTMLParagraphElement>(null);
  const specialtyRef = useRef<HTMLParagraphElement>(null);

  const [showProviderNameTooltip, setShowProviderNameTooltip] = useState(false);
  const [showAddressTooltip, setShowAddressTooltip] = useState(false);
  const [showSpecialtyTooltip, setShowSpecialtyTooltip] = useState(false);
  const [activeView, setActiveView] = useState(ViewSelectorKey.CrossCountyResults);

  useIsOverflow(providerNameRef, (isOverflow: boolean) => setShowProviderNameTooltip(isOverflow));
  useIsOverflow(addressRef, (isOverflow: boolean) => setShowAddressTooltip(isOverflow));
  useIsOverflow(specialtyRef, (isOverflow: boolean) => setShowSpecialtyTooltip(isOverflow));

  // Individual Specialty, not the Specialty Group — the group is already shown elsewhere on the profile.
  const specialtyDisplay = selectedAccessPoint?.specialtyDescription
    ? formatSpecialtyWithCode({ code: selectedAccessPoint.specialtyCode, name: selectedAccessPoint.specialtyDescription })
    : "---";

  const serviceAreaTableData = useMemo(() => {
    const data = (accessPointDetails?.serviceAreaResult || []).map<AccessPointServiceAreaData>((sa) => {
      let membershipQueryParam = '';
      if (projectContext.runConfiguration.projectRunConfigurationType !== ProjectRunConfigurationType.Standard) {
        if (providerHubContext.filterState.membershipType === MembershipType.Primary)
          membershipQueryParam = `?projectRunConfigurationType=${ProjectRunConfigurationType.DualMembershipPrimary}`;
        if (providerHubContext.filterState.membershipType === MembershipType.Secondary)
          membershipQueryParam = `?projectRunConfigurationType=${ProjectRunConfigurationType.DualMembershipSupplemental}`;
      }

      let linkHref = `${window.customUIAbsolutePath}/projects/${projectContext.project.projectId}/adequacy`;
      if (!!sa.countySSA)
        linkHref += `/county/${sa.countySSA}`;
      if (!!selectedAccessPoint.specialtyGroupCode)
        linkHref += `/specialty/${selectedAccessPoint.specialtyGroupCode}`;

      linkHref += membershipQueryParam;

      /* oxlint-disable sort-keys */
      return {
        [wafflesKeyColumnName]: sa.regionSetId.toString(),
        serviceArea: (
          <TooltipWrapper text="View in Adequacy tab" position="left">
            <a href={linkHref} target="_blank" rel="noreferrer" className="provider-profile__access-point-details-county-link" data-testid={`provider-hub__ap-details-county-link--${sa.countySSA}`}>
              {`${sa?.countySSA || "---"} - ${sa?.countyName || "---"}, ${sa?.state || "---"}`} <QesIcon iconName={QesIcons.ShareV4} description="open link in new tab" />
            </a>
          </TooltipWrapper>
        ),
        status: sa.regionSetStatus || "---",
        passPercentDisplay: isNullOrUndefined(sa.passPercent) ? "---" : `${sa.passPercent.toFixed(1)}%`,
        servesPercentDisplay: isNullOrUndefined(sa.servesPercent) ? "---" : `${sa.servesPercent.toFixed(1)}%`,
        gainsPercentDisplay: isNullOrUndefined(sa.gainsPercent) ? "---" : `${sa.gainsPercent.toFixed(1)}%`,
        impactPercentDisplay: isNullOrUndefined(sa.impactPercent) ? "---" : `${sa.impactPercent.toFixed(1)}%`,
        servesPercent: sa.servesPercent,
        gainsPercent: sa.gainsPercent,
        impactPercent: sa.impactPercent,
        passPercent: sa.passPercent,
        adequacyCriticalityScore: sa.adequacyCriticalityScore,
        adequacyCriticalityScoreDisplay: isNullOrUndefined(sa.adequacyCriticalityScore) ? "---" : `${sa.adequacyCriticalityScore}`
      };
      /* oxlint-enable sort-keys */
    });

    if (selectedAccessPoint) {
      const isOutOfNetwork = outOfNetworkProviderNetworkTypes.includes(selectedAccessPoint.providerNetworkType);
      if (isOutOfNetwork) {
        return data.sort((a, b) => {
          if ((b.impactPercent || 0) !== (a.impactPercent || 0)) {
            return (b.impactPercent || 0) - (a.impactPercent || 0);
          }
          return (b.gainsPercent || 0) - (a.gainsPercent || 0);
        });
      } else {
        return data.sort((a, b) => (b.servesPercent || 0) - (a.servesPercent || 0));
      }
    }

    return data;
  }, [accessPointDetails?.serviceAreaResult, selectedAccessPoint, providerHubContext.filterState]);

  const veryLowOnly = featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_VERY_LOW_LOCATION_CONFIDENCE_ONLY);

  const marketNetworkIntegrityEnabled = featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_MARKET_PROVIDER_RESULTS);

  const isMarketApStillProcessing = providerHubContext.isNiMarketProviderProcessing
    && selectedAccessPoint?.providerNetworkType === ProviderNetworkTypeEnum.Market;

    const getProviderTypeTaxonomyOutcome = (outcome?: string): JSX.Element => {
    if (outcome == null)
      return <span>---</span>;
    if (outcome === 'Mismatch')
      return <><span className="provider-profile__network-integrity-value__network-integrity-failure">{outcome}</span><NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={true}/></>;
    return <span>{outcome}</span>;
  };

  const getNetworkIntegrityAlertOutcome = (isLicensed: boolean, outcome?: boolean | string) => {
    if (!isLicensed)
      return <span>Package Not Licensed</span>;

    if (outcome == null)
      return <span>---</span>;

    if (typeof outcome === 'string') {
      const isFailure = veryLowOnly
        ? outcome.toLowerCase() === 'very low'
        : outcome.toLowerCase().includes('low');
      return isFailure ? <><span className="provider-profile__network-integrity-value__network-integrity-failure">{outcome}</span><NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={true}/></>
        : <span>{outcome}</span>;
    }

    return outcome ? <><span className="provider-profile__network-integrity-value__network-integrity-failure">Yes</span><NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={true}/></>
      : <span>No</span>
  };

  /* oxlint-disable sort-keys */
  const providerTypeTaxonomyRow: AccessPointNetworkIntegrityData[] =
    featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_PROVIDER_TYPE_TAXONOMY_COMPARISON)
    && selectedAccessPoint?.providerNetworkType === ProviderNetworkTypeEnum.Market
    && selectedAccessPoint?.isProviderUniverseProviderSet
      ? [{
          [wafflesKeyColumnName]: 'provider-type-taxonomy-comparison',
          alert: 'Provider Type - Taxonomy Compare',
          outcome: getProviderTypeTaxonomyOutcome(selectedAccessPoint?.taxonomyMismatch),
        }]
      : [];

  const networkIntegrityProviderAccessPointInfoData: AccessPointNetworkIntegrityData[] = [
    {
      [wafflesKeyColumnName]: 'location-confidence',
      alert: 'Location Confidence',
      outcome: getNetworkIntegrityAlertOutcome(projectContext.project.showNetworkIntegrityCriticalAlerts, selectedAccessPoint?.practitionerAtLocationConfidence)
    },
    {
      [wafflesKeyColumnName]: 'closed-location',
      alert: 'Closed Location',
      outcome: getNetworkIntegrityAlertOutcome(projectContext.project.showNetworkIntegrityAttestationBasedAlerts, selectedAccessPoint?.hasLocationClosed)
    },
    {
      [wafflesKeyColumnName]: 'left-office',
      alert: 'Left Office',
      outcome: getNetworkIntegrityAlertOutcome(projectContext.project.showNetworkIntegrityAttestationBasedAlerts, selectedAccessPoint?.hasLeftOffice)
    },
    {
      [wafflesKeyColumnName]: 'not-recognized',
      alert: 'Not Recognized',
      outcome: getNetworkIntegrityAlertOutcome(projectContext.project.showNetworkIntegrityAttestationBasedAlerts, selectedAccessPoint?.isNotRecognized)
    },
    {
      [wafflesKeyColumnName]: 'no-longer-on-roster',
      alert: 'No Longer on Roster',
      outcome: getNetworkIntegrityAlertOutcome(projectContext.project.showNetworkIntegrityAttestationBasedAlerts, selectedAccessPoint?.isNoLongerOnRoster)
    },
    {
      [wafflesKeyColumnName]: 'termed-on-roster',
      alert: 'Termed on Roster',
      outcome: getNetworkIntegrityAlertOutcome(projectContext.project.showNetworkIntegrityAttestationBasedAlerts, selectedAccessPoint?.isTermedOnRoster)
    },
    ...providerTypeTaxonomyRow,
  ];
  /* oxlint-enable sort-keys */

  const customSort = (rows: AccessPointServiceAreaData[] = [], column: keyof AccessPointServiceAreaData, direction: SortDirections) => {
    const multiplier = direction === SortDirections.Asc ? 1 : -1;

    const columnMapping: Record<string, keyof AccessPointServiceAreaData> = {
      adequacyCriticalityScoreDisplay: "adequacyCriticalityScore",
      gainsPercentDisplay: "gainsPercent",
      impactPercentDisplay: "impactPercent",
      passPercentDisplay: "passPercent",
      servesPercentDisplay: "servesPercent",
    };

    const rawColumn = columnMapping[column] || column;

    switch (rawColumn) {
      case "adequacyCriticalityScore":
      case "gainsPercent":
      case "impactPercent":
      case "passPercent":
      case "servesPercent":
        return numberSort(rows, rawColumn, multiplier);
      case "status":
        return rows.sort((a, b) => (a.status > b.status ? 1 : -1) * multiplier);
      default:
        return rows;
    }
  }

  return (
    selectedAccessPoint && (
      <>
        <h6 className="provider-profile__subtitle1">
          Access Point Details
          <TooltipWrapper
            text={<div>
              <p style={{ marginBottom: "0", textAlign: "left" }}>
                An Access Point represents a unique combination of a provider&#39;s NPI, the physical service location (address and geographic coordinates), and the specialty practiced at that location.
              </p>
            </div>}
            position="right"
            style={{ textAlign: "start" }}
          >
            <QesIcon
              iconName={QesIcons.InfoCircle}
              className="ms-2 provider-profile__info-icon"
              style={{ cursor: "help" }}
              description="Info"
            />
          </TooltipWrapper>
        </h6>
        <div className="provider-profile__access-point-details">
          <div className="provider-profile__access-point-details-header">
            {isLoadingApDetails ? <QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin" description="loading access point details" /> : (
              <>
                <div className="provider-profile__access-point-details-content">
                  <TooltipWrapper text={showProviderNameTooltip ? selectedAccessPoint.providerName : ""} position="bottom" triggerWidth="100%">
                    <p ref={providerNameRef} className="provider-profile__subtitle2--ellipsis">
                      {selectedAccessPoint.providerName}
                    </p>
                  </TooltipWrapper>
                  <TooltipWrapper text={showAddressTooltip ? `${selectedAccessPoint.address}${selectedAccessPoint.address2 ? ` ${selectedAccessPoint.address2}` : ""} ${selectedAccessPoint.city}, ${selectedAccessPoint.state} ${selectedAccessPoint.zip}` : ""} position="bottom" triggerWidth="100%">
                    <p ref={addressRef} className="provider-profile__subtitle2--ellipsis">
                      {selectedAccessPoint.address}{selectedAccessPoint.address2 ? ` ${selectedAccessPoint.address2}` : ""} {selectedAccessPoint.city}, {selectedAccessPoint.state} {selectedAccessPoint.zip}
                    </p>
                  </TooltipWrapper>
                  <TooltipWrapper text={showSpecialtyTooltip ? specialtyDisplay : ""} position="bottom" triggerWidth="100%">
                    <p ref={specialtyRef} className="provider-profile__subtitle2--ellipsis">
                      {specialtyDisplay}
                    </p>
                  </TooltipWrapper>
                </div>
                {(!providerHubContext.isScenarioModelingProcessing && !providerHubContext.isScenarioModelingError && featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_HUB_PROJECTION)) &&
                  <div>
                    {outOfNetworkProviderNetworkTypes.includes(selectedAccessPoint.providerNetworkType) &&
                      ![ProviderActionStatusEnum.FullyExcluded, ProviderActionStatusEnum.FullyTargeted].includes(selectedAccessPoint.providerActionStatus) && (
                        <RoleRestricted allowedRoles={[Roles.PROVIDER_EXCLUDE_USER]}>
                          <FlyoutLink
                            content={{
                              body: <ProviderHubProfileActionFlyout
                                accessPoints={[selectedAccessPoint]}
                                providerAction={ProviderProfileActionEnum.Exclude}
                                allAccessPoints={false}
                                individualAccessPoint={true}
                                onSubmit={props.onProviderActionSubmit}
                              />,
                              title: "Exclude Provider"
                            }}
                            testId="provider-hub-provider-profile__access-point-exclude-flyout"
                            classNames="qes-button qes-button-outline inherit"
                            size="60"
                          >
                            Exclude Access Point
                          </FlyoutLink>
                        </RoleRestricted>
                      )}
                    {selectedAccessPoint.providerNetworkType === ProviderNetworkTypeEnum.Network && selectedAccessPoint.providerActionStatus !== ProviderActionStatusEnum.FullyTerminated && (
                      <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                        <FlyoutLink
                          content={{
                            body: <ProviderHubProfileActionFlyout
                              accessPoints={[selectedAccessPoint]}
                              providerAction={ProviderProfileActionEnum.Terminate}
                              allAccessPoints={false}
                              individualAccessPoint={true}
                              onSubmit={props.onProviderActionSubmit}
                            />,
                            title: "Terminate Provider"
                          }}
                          testId="provider-hub-provider-profile__access-point-terminate-flyout"
                          classNames="qes-button qes-button-outline danger"
                          size="40"
                        >
                          Terminate Access Point
                        </FlyoutLink>
                      </RoleRestricted>
                    )}
                    {outOfNetworkProviderNetworkTypes.includes(selectedAccessPoint.providerNetworkType) &&
                      ![ProviderActionStatusEnum.FullyExcluded, ProviderActionStatusEnum.FullyTargeted].includes(selectedAccessPoint.providerActionStatus) && (
                        <RoleRestricted allowedRoles={[Roles.PROVIDER_TARGET_USER]}>
                          <FlyoutLink
                            content={{
                              body: <ProviderHubProfileActionFlyout
                                accessPoints={[selectedAccessPoint]}
                                providerAction={ProviderProfileActionEnum.Target}
                                allAccessPoints={false}
                                individualAccessPoint={true}
                                onSubmit={props.onProviderActionSubmit}
                              />,
                              title: "Target Provider"
                            }}
                            testId="provider-hub-provider-profile__access-point-target-flyout"
                            classNames="qes-button-primary ms-2"
                            size="40"
                          >
                            Target Access Point
                          </FlyoutLink>
                        </RoleRestricted>
                      )}
                  </div>
                }
              </>
            )}
          </div>

          <div className="provider-profile__access-point-details-body">
            <Accordion
              className="provider-profile__access-point-details-accordion"
              testId="provider-profile__access-point-details-accordion"
              title="Details"
              disabled={!accessPointDetails || isLoadingApDetails}
            >
              {!!accessPointDetails && (
                <dl>
                  <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
                    <div className="provider-profile__overview-row">
                      <dt className="provider-profile__overview-label">TIN</dt>
                      <dd className={`provider-profile__overview-value`}>{accessPointDetails?.tin || "---"}</dd>
                    </div>
                  </RoleRestricted>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Geo Info</dt>
                    <dd className={`provider-profile__overview-value`}>{GeoInfoToDisplay(accessPointDetails?.geoInfo) || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Latitude</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.latitude || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Longitude</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.longitude || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Phone</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.phone || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Fax</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.fax || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Organization Name</dt>
                    <dd className={`provider-profile__overview-value`}>{selectedAccessPoint?.organizationName || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">First Name</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.firstName || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Middle Name</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.middleName || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Last Name</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.lastName || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Prefix</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.prefix || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Suffix</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.suffix || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Gender</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.gender || "---"}</dd>
                  </div>
                  <div className="provider-profile__overview-row">
                    <dt className="provider-profile__overview-label">Credentials</dt>
                    <dd className={`provider-profile__overview-value`}>{accessPointDetails?.credentials || "---"}</dd>
                  </div>
                </dl>
              )}
            </Accordion>
            {featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_NETWORK_INTEGRITY_IN_PROVIDER_HUB) && (hasNonMarketAccessPoints || marketNetworkIntegrityEnabled) && (
              <>
                <ViewSelectorList defaultActiveKey={activeView} onChange={(key) => { setActiveView(key) }}>
                  {(onClick) => ([
                    <ViewSelectorItem onClick={onClick} activeView={activeView} key="cross-county-results" selectorKey={ViewSelectorKey.CrossCountyResults}>Cross-county Results</ViewSelectorItem>,
                    <ViewSelectorItem onClick={onClick} activeView={activeView} key="network-integrity" selectorKey={ViewSelectorKey.NetworkIntegrity}>Network Integrity</ViewSelectorItem>,
                  ])}
                </ViewSelectorList>
                <hr />
              </>
            )}
            {activeView == ViewSelectorKey.CrossCountyResults && !!accessPointDetails && (
              <Waffles<AccessPointServiceAreaData>
                columns={serviceAreaColumnConfig()}
                customSort={customSort}
                data={serviceAreaTableData}
                // sticky header is programmed around the window scrolling, not the current container
                // so the header appears at the top of the window...not inside the container
                disableStickyHeader={true}
                fullWidth={true}
                testId="provider-hub__provider-profile-access-point-details"
                theme={WafflesThemes.AccessPointDetails}
                variant="frozen-border"
              />
            )}
            {activeView == ViewSelectorKey.NetworkIntegrity && (hasNonMarketAccessPoints || marketNetworkIntegrityEnabled) && (
              <div className="provider-profile__network-integrity-access-point-details">
                {isMarketApStillProcessing ? (
                  <Waffles<AccessPointStillProcessingData>
                    columns={networkIntegrityStillProcessingColumnConfig()}
                    data={[{
                      status: 'Still Processing',
                      [wafflesKeyColumnName]: 'still-processing',
                    }]}
                    testId="provider-hub__provider-profile-network-integrity-access-point-details"
                    disableStickyHeader={true}
                    fullWidth={true}
                    hideTableHead={true}
                    theme={WafflesThemes.AccessPointDetails}
                  />
                ) : (
                  <Waffles<AccessPointNetworkIntegrityData>
                    columns={networkIntegrityColumnConfig()}
                    data={networkIntegrityProviderAccessPointInfoData}
                    testId="provider-hub__provider-profile-network-integrity-access-point-details"
                    disableStickyHeader={true}
                    fullWidth={true}
                    theme={WafflesThemes.AccessPointDetails}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </>
    )
  )
}